import React, { useState, useRef, useEffect } from 'react';
import { X, Eye, Pencil, Trash2, Plus, Search } from 'lucide-react';
import { colors } from '../utils/theme';
import useIsMobile from '../hooks/useIsMobile';

// ==================== BADGE ====================
export function Badge({ text, color }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: `${color || colors.textMuted}22`, color: color || colors.textMuted,
      border: `1px solid ${color || colors.textMuted}44`
    }}>{text}</span>
  );
}

// ==================== STAT CARD ====================
export function StatCard({ label, value, color, subtitle, icon: Icon }) {
  // No celular os cards vão de dois em dois: valor menor pra caber R$ 000.000,00.
  const mobile = useIsMobile();
  return (
    <div style={{
      background: colors.card, borderRadius: 12, padding: mobile ? '12px 14px' : '16px 20px',
      border: `1px solid ${colors.border}`, minWidth: mobile ? 0 : undefined,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ minWidth: mobile ? 0 : undefined }}>
          <div style={{ fontSize: mobile ? 11 : 12, color: colors.textMuted, fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
          <div style={{ fontSize: mobile ? 17 : 26, fontWeight: 700, color: color || colors.text, overflowWrap: mobile ? 'break-word' : undefined }}>{value}</div>
          {subtitle && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>{subtitle}</div>}
        </div>
        {Icon && !mobile && <Icon size={20} style={{ color: color || colors.textDim, opacity: 0.6 }} />}
      </div>
    </div>
  );
}

// ==================== LOADING ====================
export function LoadingSpinner() {
  return (
    <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
      <div style={{
        width: 32, height: 32, border: `3px solid ${colors.border}`, borderTopColor: colors.primary,
        borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      Carregando...
    </div>
  );
}

// ==================== MODAL ====================
export function Modal({ isOpen, onClose, title, width = 600, children }) {
  const mobile = useIsMobile();
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: mobile ? 8 : 20,
    }} onClick={onClose}>
      <div style={{
        background: colors.card, borderRadius: mobile ? 12 : 16, width: '100%', maxWidth: width,
        maxHeight: mobile ? '96vh' : '90vh', overflow: 'auto', border: `1px solid ${colors.border}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: mobile ? '12px 16px' : '16px 24px', borderBottom: `1px solid ${colors.border}`, gap: 8,
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, minWidth: 0 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: 4,
          }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: mobile ? 16 : 24 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ==================== FORM ELEMENTS ====================
export function FormField({ label, required, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>
        {label} {required && <span style={{ color: colors.danger }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      style={{
        width: '100%', padding: '10px 12px', borderRadius: 8,
        border: `1px solid ${colors.border}`, background: colors.bg,
        color: colors.text, fontSize: 14, boxSizing: 'border-box',
        outline: 'none', transition: 'border-color 0.2s',
        colorScheme: 'dark',
        ...props.style,
      }}
      onFocus={e => { e.target.style.borderColor = colors.primary; }}
      onBlur={e => { e.target.style.borderColor = colors.border; }}
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      style={{
        width: '100%', padding: '10px 12px', borderRadius: 8,
        border: `1px solid ${colors.border}`, background: colors.bg,
        color: colors.text, fontSize: 14, boxSizing: 'border-box',
        outline: 'none', colorScheme: 'dark', ...props.style,
      }}
    >{children}</select>
  );
}

// ==================== SEARCHABLE SELECT ====================
export function SearchableSelect({ value, onChange, options = [], placeholder = 'Selecione...', style = {}, small = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selectedLabel = options.find(o => o.value === value)?.label || '';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const filtered = options.filter(o =>
    !search || o.label.toLowerCase().includes(search.toLowerCase()) || (o.group || '').toLowerCase().includes(search.toLowerCase())
  );

  const sz = small ? { fontSize: 11, padding: '3px 6px' } : { fontSize: 14, padding: '10px 12px' };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', ...style }}>
      <div
        onClick={() => { setOpen(!open); setSearch(''); }}
        style={{
          ...sz, borderRadius: small ? 4 : 8,
          border: `1px solid ${open ? colors.primary : colors.border}`,
          background: colors.bg, color: value ? colors.text : colors.textDim,
          cursor: 'pointer', boxSizing: 'border-box',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedLabel || placeholder}</span>
        <span style={{ color: colors.textDim, fontSize: 10, marginLeft: 4 }}>{open ? '\u25B2' : '\u25BC'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: colors.card, border: `1px solid ${colors.border}`,
          borderRadius: 8, marginTop: 2, maxHeight: 240, overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column',
        }}>
          <input
            ref={inputRef}
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar..."
            style={{
              padding: '8px 10px', border: 'none', borderBottom: `1px solid ${colors.border}`,
              background: colors.bg, color: colors.text, fontSize: small ? 11 : 13,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          <div style={{ overflowY: 'auto', maxHeight: 200 }}>
            {!value ? null : (
              <div
                onClick={() => { onChange(''); setOpen(false); }}
                style={{
                  padding: '6px 10px', cursor: 'pointer', fontSize: small ? 11 : 13,
                  color: colors.textDim, fontStyle: 'italic',
                  borderBottom: `1px solid ${colors.border}22`,
                }}
                onMouseEnter={e => { e.target.style.background = colors.cardHover; }}
                onMouseLeave={e => { e.target.style.background = 'transparent'; }}
              >
                {placeholder}
              </div>
            )}
            {filtered.length === 0 ? (
              <div style={{ padding: '10px', color: colors.textDim, fontSize: small ? 11 : 13, textAlign: 'center' }}>
                Nenhum resultado
              </div>
            ) : filtered.map((o, i) => (
              <div
                key={o.value || i}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: '6px 10px', cursor: 'pointer', fontSize: small ? 11 : 13,
                  color: o.value === value ? colors.primary : colors.text,
                  fontWeight: o.value === value ? 600 : 400,
                  background: o.value === value ? `${colors.primary}11` : 'transparent',
                  borderBottom: `1px solid ${colors.border}11`,
                }}
                onMouseEnter={e => { e.target.style.background = colors.cardHover; }}
                onMouseLeave={e => { e.target.style.background = o.value === value ? `${colors.primary}11` : 'transparent'; }}
              >
                {o.group && <span style={{ fontSize: small ? 9 : 10, color: colors.textDim, marginRight: 6 }}>{o.group}</span>}
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function TextArea(props) {
  return (
    <textarea
      {...props}
      style={{
        width: '100%', padding: '10px 12px', borderRadius: 8,
        border: `1px solid ${colors.border}`, background: colors.bg,
        color: colors.text, fontSize: 14, boxSizing: 'border-box',
        minHeight: 80, resize: 'vertical', outline: 'none',
        fontFamily: 'inherit', ...props.style,
      }}
      onFocus={e => { e.target.style.borderColor = colors.primary; }}
      onBlur={e => { e.target.style.borderColor = colors.border; }}
    />
  );
}

// ==================== BUTTONS ====================
export function Button({ children, color = colors.primary, variant = 'solid', size = 'md', icon: Icon, ...props }) {
  const isOutline = variant === 'outline';
  const isSmall = size === 'sm';
  return (
    <button
      {...props}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: isSmall ? '6px 12px' : '10px 20px',
        borderRadius: 8, border: isOutline ? `1px solid ${color}` : 'none',
        background: isOutline ? 'transparent' : color,
        color: isOutline ? color : '#fff',
        fontSize: isSmall ? 12 : 14, fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer',
        transition: 'opacity 0.2s', opacity: props.disabled ? 0.5 : 1,
        whiteSpace: 'nowrap', ...props.style,
      }}
    >
      {Icon && <Icon size={isSmall ? 14 : 16} />}
      {children}
    </button>
  );
}

// ==================== ACTION BUTTONS ====================
export function ActionButtons({ onView, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {onView && (
        <button onClick={onView} title="Visualizar" style={actionBtnStyle}>
          <Eye size={14} />
        </button>
      )}
      {onEdit && (
        <button onClick={onEdit} title="Editar" style={{ ...actionBtnStyle, color: colors.primary }}>
          <Pencil size={14} />
        </button>
      )}
      {onDelete && (
        <button onClick={onDelete} title="Excluir" style={{ ...actionBtnStyle, color: colors.danger }}>
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

const actionBtnStyle = {
  background: 'transparent', border: 'none', color: colors.textMuted,
  cursor: 'pointer', padding: 6, borderRadius: 6, transition: 'background 0.15s',
};

// ==================== PAGE HEADER ====================
export function PageHeader({ title, subtitle, onAdd, addLabel = 'Criar novo' }) {
  const mobile = useIsMobile();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: mobile ? 14 : 20, flexWrap: 'wrap', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ fontSize: mobile ? 18 : 22, fontWeight: 700, margin: 0 }}>{title}</h2>
        {subtitle && <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {onAdd && (
        <Button icon={Plus} onClick={onAdd} style={{ flexShrink: 0 }}>{addLabel}</Button>
      )}
    </div>
  );
}

// ==================== SEARCH BAR ====================
export function SearchBar({ value, onChange, placeholder = 'Buscar...', children }) {
  // Celular: busca na linha toda e os filtros de dois em dois, mesma largura
  // (a classe xm-filtros, em responsive.css, desfaz o minWidth de cada um).
  const mobile = useIsMobile();
  return (
    <div className={mobile ? 'xm-filtros' : undefined} style={mobile ? {
      display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, alignItems: 'center', marginBottom: 14,
    } : {
      display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap',
    }}>
      <div style={mobile ? { position: 'relative', gridColumn: '1 / -1' } : { position: 'relative', flex: '1 1 250px', maxWidth: 350 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
        <Input
          value={value} onChange={onChange} placeholder={placeholder}
          style={{ paddingLeft: 36 }}
        />
      </div>
      {children}
    </div>
  );
}

// ==================== TABLE ====================
export function Table({ children }) {
  return (
    <div style={{
      background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`,
      overflow: 'hidden',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          {children}
        </table>
      </div>
    </div>
  );
}

export function Th({ children, align = 'left', style: extraStyle, sortKey, sortState, onSort }) {
  const isActive = sortState && sortKey && sortState.sortKey === sortKey;
  const arrow = isActive ? (sortState.sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const clickable = !!onSort && !!sortKey;

  return (
    <th
      onClick={clickable ? () => onSort(sortKey) : undefined}
      style={{
        padding: '10px 14px', textAlign: align, borderBottom: `2px solid ${colors.border}`,
        color: isActive ? colors.primaryLight : colors.textMuted,
        fontWeight: 600, fontSize: 11, textTransform: 'uppercase',
        letterSpacing: 0.3, background: `${colors.bg}80`, whiteSpace: 'nowrap',
        cursor: clickable ? 'pointer' : 'default',
        userSelect: 'none', transition: 'color 0.15s',
        ...extraStyle,
      }}
    >{children}{arrow}</th>
  );
}

export function Td({ children, style: extraStyle, ...props }) {
  return (
    <td style={{
      padding: '10px 14px', borderBottom: `1px solid ${colors.border}15`, ...extraStyle,
    }} {...props}>{children}</td>
  );
}

export function TrHover({ children, onClick, ...props }) {
  return (
    <tr
      {...props}
      style={{ cursor: onClick ? 'pointer' : 'default', transition: 'background 0.1s' }}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.background = colors.cardHover}
      onMouseLeave={e => e.currentTarget.style.background = ''}
    >{children}</tr>
  );
}

// ==================== EMPTY STATE ====================
export function EmptyState({ message = 'Nenhum registro encontrado', icon: Icon }) {
  return (
    <div style={{
      textAlign: 'center', padding: '40px 20px', color: colors.textMuted,
    }}>
      {Icon && <Icon size={40} style={{ opacity: 0.3, marginBottom: 12 }} />}
      <div>{message}</div>
    </div>
  );
}

// ==================== CONFIRM DIALOG ====================
export function ConfirmDialog({ isOpen, onClose, onConfirm, title, message }) {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: colors.card, borderRadius: 16, width: '100%', maxWidth: 420,
        border: `1px solid ${colors.border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        padding: 24,
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>{title || 'Confirmar'}</h3>
        <p style={{ color: colors.textMuted, fontSize: 14, margin: '0 0 24px' }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outline" color={colors.textMuted} onClick={onClose}>Cancelar</Button>
          <Button color={colors.danger} onClick={onConfirm}>Confirmar</Button>
        </div>
      </div>
    </div>
  );
}
